import React from 'react';

const PLAN_HIERARCHY = {
  professional: 1,
  business:     2,
  // Legacy aliases (DB may still have old values until Stripe webhook runs)
  starter:      1,
  pro:          1,
  team:         2,
  agency:       2,
};

const PLAN_NAMES = {
  professional: 'Professional',
  business:     'Business',
};

const PLAN_PRICES = {
  professional: '$399/mo',
  business:     '$799/mo',
};

export function hasAccess(userPlan, requiredPlan) {
  const userLevel     = PLAN_HIERARCHY[userPlan]     || 0;
  const requiredLevel = PLAN_HIERARCHY[requiredPlan] || 1;
  return userLevel >= requiredLevel;
}

export default function PlanGate({ userPlan, requiredPlan, onUpgrade, children, featureName }) {
  if (hasAccess(userPlan, requiredPlan)) {
    return children;
  }

  const required = PLAN_NAMES[requiredPlan] || 'Professional';
  const price    = PLAN_PRICES[requiredPlan] || '$399/mo';

  return (
    <div style={{
      height: '100%', display: 'flex', alignItems: 'center',
      justifyContent: 'center', padding: 40,
    }}>
      <div style={{
        maxWidth: 460, textAlign: 'center',
      }}>
        {/* Lock Icon */}
        <div style={{
          width: 64, height: 64, borderRadius: 16, margin: '0 auto 20px',
          background: 'var(--amber-bg)', border: '1px solid var(--amber)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'var(--amber)',
        }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
          </svg>
        </div>

        <div style={{
          fontSize: 20, fontWeight: 700,
          color: 'var(--text)', marginBottom: 10,
        }}>
          {featureName || 'This feature'} requires {required}
        </div>

        <div style={{
          fontSize: 13, color: 'var(--text3)', lineHeight: 1.6, marginBottom: 24,
        }}>
          You are currently on the {PLAN_NAMES[userPlan] || 'Free Trial'} plan.
          Upgrade to {required} at {price} to unlock {featureName || 'this feature'}
          and everything else included in the {required} plan.
        </div>

        {/* What you get */}
        <div style={{
          background: 'var(--bg2)', border: '1px solid var(--border)',
          borderRadius: 10, padding: '16px 20px', marginBottom: 24,
          textAlign: 'left',
        }}>
          <div style={{
            fontSize: 11, fontWeight: 600, color: 'var(--text3)',
            textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10,
          }}>
            {required} plan includes
          </div>
          {getPlanFeatures(requiredPlan).map(f => (
            <div key={f} style={{
              display: 'flex', alignItems: 'center', gap: 8,
              fontSize: 12, color: 'var(--text2)', marginBottom: 7,
            }}>
              <div style={{
                width: 14, height: 14, borderRadius: '50%', flexShrink: 0,
                background: 'var(--green-bg)', border: '1px solid var(--green)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 8, fontWeight: 700, color: 'var(--green)',
              }}>v</div>
              {f}
            </div>
          ))}
        </div>

        <button
          onClick={onUpgrade}
          style={{
            width: '100%', padding: '13px', borderRadius: 8,
            fontSize: 14, fontWeight: 700, cursor: 'pointer',
            border: '1px solid var(--accent)',
            background: 'var(--accent)', color: '#fff',
            fontFamily: 'var(--font)', marginBottom: 10,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = '#2570C0'}
          onMouseLeave={e => e.currentTarget.style.background = 'var(--accent)'}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="18 15 12 9 6 15"/>
          </svg>
          Upgrade Now — {required} at {price}
        </button>

        <button
          onClick={onUpgrade}
          style={{
            width: '100%', padding: '10px', borderRadius: 8,
            fontSize: 13, fontWeight: 500, cursor: 'pointer',
            border: '1px solid var(--border2)',
            background: 'transparent', color: 'var(--text3)',
            fontFamily: 'var(--font)', marginBottom: 12,
            transition: 'all 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border2)'; e.currentTarget.style.color = 'var(--text3)'; }}
        >
          View all plans &rarr;
        </button>

        <div style={{ fontSize: 11, color: 'var(--text3)' }}>
          Cancel anytime &middot; 7-day money back guarantee
        </div>
      </div>
    </div>
  );
}

function getPlanFeatures(plan) {
  const features = {
    professional: [
      'All 3 CMMC levels + NIST 800-171',
      'SSP and POA&M PDF export',
      'Policy template library',
      'AI compliance assistant',
      'Auditor access for C3PAOs',
      'Annual affirmation tracker',
      'Up to 3 team members',
      '3 frameworks included',
    ],
    business: [
      'Everything in Professional',
      'All 8 compliance frameworks',
      'Unlimited team seats',
      'Priority support',
      'Dedicated onboarding call',
      'SSP spreadsheet export',
    ],
  };
  return features[plan] || features.professional;
}
